import { useState, useEffect, useCallback, useMemo } from "react";
import { Container, Row, Col, Card, Form, Table, Spinner, Button } from "react-bootstrap";
import { BiRefresh, BiUserPlus, BiCheckCircle, BiTime, BiXCircle, BiTrendingUp } from "react-icons/bi";
import { getSellerOnboardingInsights } from "../api/getapi";
import { formatNumber } from "../Utilities/numbers";
import { ECHART_COLORS } from "../Utilities/echartsTheme";
import StatCard from "../Components/DashboardCards/StatCard/StatCard";
import EChartsFunnel from "../Components/Charts/EChartsFunnel";
import EChartsTimeSeriesLine from "../Components/Charts/EChartsTimeSeriesLine";
import EChartsHorizontalBar from "../Components/Charts/EChartsHorizontalBar";

const spinKeyframes = `@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } } .spin { animation: spin 0.8s linear infinite; display: inline-block; }`;

const timePeriods = [
  { value: "all-time", label: "All Time" },
  { value: "today", label: "Today" },
  { value: "yesterday", label: "Yesterday" },
  { value: "this-week", label: "This Week" },
  { value: "this-month", label: "This Month" },
  { value: "this-year", label: "This Year" },
  { value: "last-year", label: "Last Year" },
];

const SellerOnboardingDashboard = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [timePeriod, setTimePeriod] = useState("all-time");

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getSellerOnboardingInsights({ timePeriod });
      setData(res.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [timePeriod]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const conversionRate = useMemo(() => {
    if (!data?.summary?.total) return 0;
    return ((data.summary.converted / data.summary.total) * 100).toFixed(1);
  }, [data]);

  const funnelData = useMemo(() => {
    if (!data?.summary) return [];
    const s = data.summary;
    return [
      { name: "Total Requests", value: s.total },
      { name: "Assigned", value: s.assigned },
      { name: "Converted", value: s.converted },
    ];
  }, [data]);

  const monthlyTrendData = useMemo(() => {
    if (!data?.monthlyTrend) return [];
    return [...data.monthlyTrend].reverse().map((m) => ({
      month: m.month,
      total: Number(m.total) || 0,
      converted: Number(m.converted) || 0,
    }));
  }, [data]);

  const handlerChartData = useMemo(() => {
    if (!data?.byHandler) return [];
    return data.byHandler
      .filter((h) => h.user_full_name !== "Unassigned")
      .slice(0, 10)
      .map((h) => ({ label: h.user_full_name, value: h.total }));
  }, [data]);

  return (
    <Container fluid className="px-xl-5">
      <style>{spinKeyframes}</style>
      <Row className="py-2 align-items-center">
        <Col md="6">
          <h5 className="fw-bold">Seller Onboarding Dashboard</h5>
          <p className="text-muted mb-0">Registration requests, conversion funnel, and sales team performance.</p>
        </Col>
        <Col md="6" className="text-md-end mt-3 mt-md-0">
          <Button variant="light" size="sm" className="me-2 border" onClick={fetchData} disabled={loading}>
            <BiRefresh size={20} className={loading ? "spin" : ""} />
          </Button>
          <Form.Select
            value={timePeriod}
            onChange={(e) => setTimePeriod(e.target.value)}
            style={{ width: 180, display: "inline-block" }}
          >
            {timePeriods.map((t) => (
              <option key={t.value} value={t.value}>
                {t.label}
              </option>
            ))}
          </Form.Select>
        </Col>
      </Row>

      {loading && (
        <div className="text-center mt-4">
          <Spinner animation="border" />
        </div>
      )}

      {!loading && data && (
        <>
          {/* KPI Stat Cards */}
          <Row className="g-3 mt-1 mb-4">
            <Col md="6" lg="3" sm="12" xl="3">
              <StatCard
                title="Total Requests"
                count={formatNumber(data.summary.total)}
                icon={<BiUserPlus />}
                color={ECHART_COLORS.primary}
              />
            </Col>
            <Col md="6" lg="3" sm="12" xl="3">
              <StatCard
                title="Converted"
                count={formatNumber(data.summary.converted)}
                icon={<BiCheckCircle />}
                color={ECHART_COLORS.success}
              />
            </Col>
            <Col md="6" lg="3" sm="12" xl="3">
              <StatCard
                title="UnAssigned"
                count={formatNumber(data.summary.unassigned)}
                icon={<BiTime />}
                color={ECHART_COLORS.accentLight}
              />
            </Col>
            <Col md="6" lg="3" sm="12" xl="3">
              <StatCard
                title="Conversion Rate"
                count={`${conversionRate}%`}
                icon={<BiTrendingUp />}
                color={ECHART_COLORS.purple}
              />
            </Col>
          </Row>

          {/* Secondary KPIs */}
          <Row className="g-3 mb-4">
            <Col md="6" lg="3" sm="12" xl="3">
              <StatCard
                title="Assigned"
                count={formatNumber(data.summary.assigned)}
                icon={<BiUserPlus />}
                color={ECHART_COLORS.teal}
              />
            </Col>
            <Col md="6" lg="3" sm="12" xl="3">
              <StatCard
                title="UnConverted"
                count={formatNumber(data.summary.unconverted)}
                icon={<BiXCircle />}
                color={ECHART_COLORS.danger}
              />
            </Col>
          </Row>

          {/* Charts Row */}
          <Row className="g-3 mb-4">
            <Col md="12" lg="5" sm="12" xl="5">
              <Card className="h-100 border-0 shadow-sm p-3">
                <h6 className="fw-bold mb-3">Conversion Funnel</h6>
                <div style={{ height: 300, overflow: "hidden" }}>
                  <EChartsFunnel data={funnelData} title="Seller Onboarding" />
                </div>
              </Card>
            </Col>
            <Col md="12" lg="7" sm="12" xl="7">
              <Card className="h-100 border-0 shadow-sm p-3">
                <h6 className="fw-bold mb-3">Monthly Request Trend</h6>
                {monthlyTrendData.length > 0 && (
                  <div style={{ height: 300, overflow: "hidden" }}>
                    <EChartsTimeSeriesLine
                      data={monthlyTrendData}
                      label="Total Requests"
                      color={ECHART_COLORS.primary}
                      valueKey="total"
                    />
                  </div>
                )}
              </Card>
            </Col>
          </Row>

          {/* Handler Performance */}
          <Row className="g-3 mb-4">
            <Col md="12">
              <Card className="border-0 shadow-sm p-3">
                <h6 className="fw-bold mb-3">Requests by Sales Team Member</h6>
                {handlerChartData.length > 0 && (
                  <div style={{ height: 300, overflow: "hidden" }}>
                    <EChartsHorizontalBar
                      data={handlerChartData}
                      label="Requests Handled"
                      color={ECHART_COLORS.primary}
                    />
                  </div>
                )}
              </Card>
            </Col>
          </Row>

          {/* Recent Requests Table */}
          <Row className="g-3 mb-4">
            <Col md="12">
              <Card className="border-0 shadow-sm p-3">
                <h6 className="fw-bold mb-3">Recent Seller Registration Requests</h6>
                <div className="table-responsive">
                  <Table hover size="sm" className="align-middle">
                    <thead className="bg-light">
                      <tr>
                        <th className="text-center" style={{ width: 40 }}>#</th>
                        <th>Name</th>
                        <th>Email</th>
                        <th>Phone</th>
                        <th>Shop Name</th>
                        <th>City</th>
                        <th>Status</th>
                        <th>Handled By</th>
                        <th>Date</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(data.recentRequests || []).map((row, i) => (
                        <tr key={row.id}>
                          <td className="text-center text-muted">{i + 1}</td>
                          <td className="fw-medium">{row.first_name} {row.last_name}</td>
                          <td>{row.email}</td>
                          <td>{row.phone_number}</td>
                          <td>{row.shop_name || "—"}</td>
                          <td>{row.city}</td>
                          <td>
                            <span
                              className="badge"
                              style={{
                                backgroundColor:
                                  row.status === "Converted" ? ECHART_COLORS.success + "20" :
                                  row.status === "Assigned" ? ECHART_COLORS.primary + "20" :
                                  row.status === "UnConverted" ? ECHART_COLORS.danger + "20" :
                                  ECHART_COLORS.accentLight + "20",
                                color:
                                  row.status === "Converted" ? ECHART_COLORS.success :
                                  row.status === "Assigned" ? ECHART_COLORS.primary :
                                  row.status === "UnConverted" ? ECHART_COLORS.danger :
                                  ECHART_COLORS.accentLight,
                                fontSize: "0.75rem",
                              }}
                            >
                              {row.status}
                            </span>
                          </td>
                          <td>{row.handler_name}</td>
                          <td className="text-muted" style={{ fontSize: "0.85rem" }}>
                            {row.created_at ? new Date(row.created_at).toLocaleDateString() : "—"}
                          </td>
                        </tr>
                      ))}
                      {(!data.recentRequests || data.recentRequests.length === 0) && (
                        <tr>
                          <td colSpan="9" className="text-center text-muted py-3">
                            No registration requests found
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </Table>
                </div>
              </Card>
            </Col>
          </Row>
        </>
      )}
    </Container>
  );
};

export default SellerOnboardingDashboard;
